import { Empty } from 'antd'
import PropTypes from 'prop-types'
import React, { memo, useCallback, useMemo, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import utils from '../../utils'
import { EmptyText } from '../Common/CellText'
import config from './../../config'
import { ViewerWrapper } from './PDFViewerStyled'

import authenticationStore from '../../stores/authenticationStore'
import IFrameHttp from './IFrameHttp'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

const { apiUrl, apiUrlTruc } = config

const PDFViewer = ({ file, fileId, fileType, fileName }) => {
  const [numPages, setNumPages] = useState(null)

  // Memoize các path checks để tránh tính toán lại
  const pathChecks = useMemo(
    () => ({
      isFileVIMCToTruc: window.location.pathname.includes('connected-document'),
      isUBQLV: window.location.pathname.includes(
        'connected-committee-document'
      ),
      isFileTruc: window.location.pathname.includes('messages'),
    }),
    []
  )

  const { isFileVIMCToTruc, isUBQLV, isFileTruc } = pathChecks

  // Memoize file type checks
  const fileTypeChecks = useMemo(
    () => ({
      isPDF: ['pdf'].includes(fileType),
      isImageAndPDF:
        file?.type === 'application/pdf' ||
        [
          'jpg',
          'bmp',
          'dib',
          'jpe',
          'jfif',
          'gif',
          'tif',
          'tiff',
          'png',
          'heic',
          'pdf',
        ].includes(fileType),
    }),
    [fileType, file?.type]
  )

  const { isPDF, isImageAndPDF } = fileTypeChecks

  // Memoize device check
  const isIOSDevice = useMemo(() => utils.isIOSDevice(), [])

  // Memoize view file URL generation
  const viewFile = useMemo(() => {
    if (isIOSDevice && (isPDF || isUBQLV)) {
      if (isPDF) {
        return isFileTruc
          ? `${apiUrl}/api/v1/truc-upload/view/${fileId}?fileName=${fileName}`
          : `${isFileVIMCToTruc ? apiUrlTruc : apiUrl}/api/v1/images/${fileId}`
      }
      if (isUBQLV) {
        return file
      }
    } else {
      if (isImageAndPDF) {
        if (fileId) {
          return isFileTruc
            ? `${apiUrl}/api/v1/truc-upload/view/${fileId}?fileName=${fileName}`
            : `${
                isFileVIMCToTruc ? apiUrlTruc : apiUrl
              }/api/v1/images/${fileId}`
        } else {
          return URL.createObjectURL(file) // View file UBQLV
        }
      } else {
        const baseUrl = isFileTruc
          ? `${apiUrl}/api/v1/truc-upload/view/${fileId}?fileName=${fileName}`
          : `${isFileVIMCToTruc ? apiUrlTruc : apiUrl}/api/v1/images/${fileId}`
        return `https://view.officeapps.live.com/op/embed.aspx?src=${baseUrl}`
      }
    }
    return null
  }, [
    isIOSDevice,
    isPDF,
    isUBQLV,
    isFileTruc,
    isFileVIMCToTruc,
    fileId,
    fileName,
    file,
    isImageAndPDF,
  ])

  // Callback để tránh tạo lại function
  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages)
  }, [])

  // Memoize empty state content
  const emptyContent = useMemo(
    () => (
      <Empty
        style={{ marginTop: 40 }}
        description={
          isIOSDevice ? (
            <EmptyText>Không có văn bản hợp lệ.</EmptyText>
          ) : (
            <EmptyText>Không có văn bản</EmptyText>
          )
        }
      />
    ),
    [isIOSDevice]
  )

  // Memoize document content
  const documentContent = useMemo(() => {
    if (!numPages) return null

    return Array.from(new Array(numPages), (el, index) => (
      <Page key={`page_${index + 1}`} pageNumber={index + 1} />
    ))
  }, [numPages])

  // Memoize document file URL
  const documentFileUrl = useMemo(() => {
    if (!viewFile) return null

    return isUBQLV
      ? viewFile
      : `${viewFile}?access_token=${
          JSON.parse(authenticationStore.appToken).access_token
        }`
  }, [viewFile, isUBQLV])

  const renderContent = useCallback(() => {
    if (!fileId && !file) return emptyContent

    if (isIOSDevice && (isPDF || isUBQLV)) {
      return (
        <Document
          file={documentFileUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          error="Không tải được tệp.">
          {documentContent}
        </Document>
      )
    }

    return (
      <IFrameHttp url={viewFile} isUBQLV={isUBQLV} isFileTruc={isFileTruc} />
    )
  }, [
    fileId,
    file,
    emptyContent,
    isIOSDevice,
    isPDF,
    isUBQLV,
    documentFileUrl,
    onDocumentLoadSuccess,
    documentContent,
    viewFile,
    isFileTruc,
  ])

  return (
    <ViewerWrapper style={{ height: fileId || file ? '90vh' : 'unset' }}>
      {renderContent()}
    </ViewerWrapper>
  )
}

PDFViewer.propTypes = {
  file: PropTypes.object,
  fileId: PropTypes.string,
  fileType: PropTypes.string,
  fileName: PropTypes.string,
  customStyleObj: PropTypes.object,
}

export default memo(PDFViewer)
